import { Link } from "react-router-dom";
import { MessageCircle, Scale } from "lucide-react";
import { catLabel, fmtDate, img, waLink } from "../../lib/api";

const STATUS_STYLE = {
  Tersedia: ["Tersedia", "bg-emerald-500 text-white"],
  Terpesan: ["Terpesan", "bg-amber-400 text-amber-950"],
  Terjual: ["Sold Out", "bg-red-600 text-white"],
};

export const StatusBadge = ({ status, testid }) => {
  const [label, cls] = STATUS_STYLE[status] || [status, "bg-gray-200"];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow ${cls}`} data-testid={testid}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {label}
    </span>
  );
};

export const CattleCard = ({ c, whatsapp }) => (
  <article className="card-lift overflow-hidden rounded-3xl border bg-white" data-testid={`cattle-card-${c.id}`}>
    <div className="relative aspect-[16/10] overflow-hidden bg-[var(--cream)]">
      <img src={img(c.image_url)} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
      <div className="absolute left-4 top-4"><StatusBadge status={c.status} testid={`cattle-status-${c.id}`} /></div>
    </div>
    <div className="p-6">
      <p className="eyebrow">{c.breed}</p>
      <h3 className="mt-1 font-display text-xl font-semibold text-[var(--forest-dark)]">{c.name}</h3>
      {c.weight_estimate && (
        <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[var(--earth)]"><Scale className="h-4 w-4" /> Estimasi bobot {c.weight_estimate}</p>
      )}
      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[var(--ink)]/70">{c.description}</p>
      {c.status !== "Terjual" && (
        <a href={waLink(whatsapp, `Halo Sembada Satwa, saya tertarik dengan sapi ${c.name} (${c.breed}).`)} target="_blank" rel="noreferrer" className="btn-forest mt-5 w-full" data-testid={`cattle-wa-btn-${c.id}`}>
          <MessageCircle className="h-4 w-4" /> Tanya via WhatsApp
        </a>
      )}
    </div>
  </article>
);

export const CattleCardCompact = ({ c, whatsapp }) => (
  <article className="card-lift overflow-hidden rounded-2xl border bg-white" data-testid={`cattle-card-${c.id}`}>
    <div className="relative aspect-[4/3] overflow-hidden bg-[var(--cream)]">
      <img src={img(c.image_url)} alt={c.name} className="h-full w-full object-cover" loading="lazy" />
      <div className="absolute left-2 top-2 scale-90 origin-top-left"><StatusBadge status={c.status} testid={`cattle-status-${c.id}`} /></div>
    </div>
    <div className="flex items-center justify-between gap-2 p-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-[var(--forest-dark)]">{c.name}</p>
        <p className="truncate text-xs text-[var(--ink)]/60">{c.breed}{c.weight_estimate ? ` · ${c.weight_estimate}` : ""}</p>
      </div>
      {c.status !== "Terjual" && (
        <a href={waLink(whatsapp, `Halo Sembada Satwa, saya tertarik dengan sapi ${c.name} (${c.breed}).`)} target="_blank" rel="noreferrer" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--forest)] text-white hover:bg-[var(--forest-dark)]" data-testid={`cattle-wa-btn-${c.id}`} aria-label="WhatsApp">
          <MessageCircle className="h-4 w-4" />
        </a>
      )}
    </div>
  </article>
);

export const ArticleCard = ({ a, large = false }) => (
  <Link to={`/edukasi/${a.slug}`} className={`card-lift group block overflow-hidden rounded-3xl border bg-white ${large ? "md:grid md:grid-cols-2" : ""}`} data-testid={`article-card-${a.slug}`}>
    <div className={`overflow-hidden bg-[var(--cream)] ${large ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[16/10]"}`}>
      <img src={img(a.thumbnail_url)} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
    </div>
    <div className={large ? "p-8 md:p-10 md:self-center" : "p-6"}>
      <div className="flex items-center gap-3 text-xs">
        <span className="rounded-full bg-[var(--gold-soft)] px-3 py-1 font-semibold text-[var(--earth)]">{catLabel(a.category)}</span>
        <span className="text-[var(--ink)]/50">{fmtDate(a.created_at)}</span>
      </div>
      <h3 className={`mt-3 font-display font-semibold text-[var(--forest-dark)] group-hover:text-[var(--forest)] ${large ? "text-2xl md:text-3xl" : "text-lg"}`}>{a.title}</h3>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[var(--ink)]/70">{a.excerpt}</p>
      <span className="mt-4 inline-block text-sm font-semibold text-[var(--forest)]">Baca selengkapnya →</span>
    </div>
  </Link>
);
